/* eslint-disable prettier/prettier */
/* eslint-disable no-useless-escape */
import { TFunction } from "react-i18next";

import { UploadCentreColumnType, UploadCentreItemName } from "../../../../../../../../util/enums";

type ColumnProps = {
  key: string;
  type: UploadCentreColumnType;
  isRequired: boolean;
  label?: string;
  csvColumnName?: string;
};

interface ColumnsProps {
  [UploadCentreItemName.FLEET]: ColumnProps[];
  [UploadCentreItemName.MATERIAL]: ColumnProps[];
  [UploadCentreItemName.SERVICE]: ColumnProps[];
  [UploadCentreItemName.STAFF]: ColumnProps[];
}

const ColumnsType = {
  FLEET: "fleet",
  SERVICE: "service",
  MATERIAL: "material",
  STAFF: "staff",
} as const;

type ColumnsKey = keyof typeof ColumnsType;
export type ColumnsValues = typeof ColumnsType[ColumnsKey];

const GET_INPUT_COLUMNS = (translate: TFunction<"translation", undefined>): ColumnsProps => {
  const COLUMNS: ColumnsProps = {
    [UploadCentreItemName.FLEET]: [
      {
        key: "fleetTypeId",
        type: UploadCentreColumnType.FLEET_ID,
        isRequired: true,
        label: translate("type"),
        csvColumnName: "\"Type* (Options: Truck, Trailer, Forklift, Van, Ute, Prime Mover)\"",
      },
      {
        key: "fleetNumber",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("fleetNumber"),
        csvColumnName: " Fleet Number*",
      },
      {
        key: "make",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("make"),
        csvColumnName: " Make*",
      },
      {
        key: "model",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("model"),
        csvColumnName: " Model*",
      },
      {
        key: "licensePlate",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("plateNumber"),
        csvColumnName: " Plate Number*",
      },
      {
        key: "typeMode",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("typeMode"),
        csvColumnName: "\"Type Mode* (Options: Pantech, Flatbed)\"",
      },
      {
        key: "warehouseCode",
        type: UploadCentreColumnType.WAREHOUSE_CODE,
        isRequired: true,
        label: translate("warehouseCode"),
        csvColumnName: " Warehouse Code*",
      },
      {
        key: "branchCode",
        type: UploadCentreColumnType.BRANCH_CODE,
        isRequired: true,
        label: translate("branchCode"),
        csvColumnName: " Branch Code* (for multiple branches use /)",
      },
      {
        key: "vinNumber",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("vinNumber"),
        csvColumnName: " Vin Number",
      },
      {
        key: "year",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("year"),
        csvColumnName: " Year (yyyy)",
      },
      {
        key: "engine",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("engine"),
        csvColumnName: " Engine Type",
      },
      {
        key: "bodyType",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("bodyType"),
        csvColumnName: " Body Type",
      },
      {
        key: "condition",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("condition"),
        csvColumnName: "\"Condition  (Options: New, Used)\"",
      },
      {
        key: "fuelType",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("fuelType"),
        csvColumnName: "\"Fuel Type (Options: Diesel, Gasoline, Ethanol, LPG, Petrol)\"",
      },
      {
        key: "ownership",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("ownership"),
        csvColumnName: "\"Ownership (Options: Fully Owned, Leasing)\"",
      },
      {
        key: "capacityM3",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("capacity"),
        csvColumnName: " Capacity",
      },
      {
        key: "outOfService",
        type: UploadCentreColumnType.BOOLEAN,
        isRequired: false,
        label: translate("outOfService"),
        csvColumnName: "\"Out of Service (mark as \"\"\"\"x\"\"\"\" for true)\"",
      },
      {
        key: "isLoadable",
        type: UploadCentreColumnType.BOOLEAN,
        isRequired: false,
        label: translate("enableAsAStorageBox"),
        csvColumnName: "\"Enable as a storage box (mark as \"\"\"\"x\"\"\"\" for true)\"",
      },
    ],
    [UploadCentreItemName.MATERIAL]: [
      {
        key: "code",
        type: UploadCentreColumnType.CODE,
        isRequired: true,
        label: translate("code"),
        csvColumnName: "Code *",
      },
      {
        key: "name",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("name"),
        csvColumnName: "Name *",
      },
      {
        key: "volume",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("volume"),
        csvColumnName: "Volume (M3)",
      },
      {
        key: "weight",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("weight"),
        csvColumnName: "Weight (KG)",
      },
      {
        key: "price",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("price"),
        csvColumnName: "Price",
      },
      {
        key: "isItem",
        type: UploadCentreColumnType.BOOLEAN,
        isRequired: false,
        label: translate("isAnItem"),
        csvColumnName: "\"Is an item? (mark as \"\"x\"\" for true)\"",
      },
    ],
    [UploadCentreItemName.SERVICE]: [
      {
        key: "code",
        type: UploadCentreColumnType.CODE,
        isRequired: true,
        label: translate("code"),
        csvColumnName: "Code *",
      },
      {
        key: "sellDescription",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("serviceDescription"),
        csvColumnName: "Service Description *",
      },
      {
        key: "sellType",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("sellType"),
        csvColumnName: "\"Sell Type (Options: Normal, Extra, Storage, Additional, Percentage, Prepayment, Packing Materials)\"",
      },
      {
        key: "sellM3Fee",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("m3Fee"),
        csvColumnName: "M3 Fee",
      },
      {
        key: "sellFlatFee",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("flatFee"),
        csvColumnName: "Flat Fee",
      },
      {
        key: "sellHourlyFee",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("hourlyFee"),
        csvColumnName: "Hourly Fee",
      },
      {
        key: "sellHalfHourFee",
        type: UploadCentreColumnType.NUMBER,
        isRequired: false,
        label: translate("halfHourFee"),
        csvColumnName: "Half Hour Fee",
      },
    ],
    [UploadCentreItemName.STAFF]: [
      {
        key: "firstName",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("firstName"),
        csvColumnName: "First Name *",
      },
      {
        key: "lastName",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("lastName"),
        csvColumnName: "Last Name *",
      },
      {
        key: "email",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("email"),
        csvColumnName: "Email *",
      },
      {
        key: "phone",
        type: UploadCentreColumnType.STRING,
        isRequired: true,
        label: translate("phone"),
        csvColumnName: "Phone *",
      },
      {
        key: "branch",
        type: UploadCentreColumnType.BRANCH_CODE,
        isRequired: true,
        label: translate("branch"),
        csvColumnName: "Branch Code*",
      },
      {
        key: "position",
        type: UploadCentreColumnType.STRING,
        isRequired: false,
        label: translate("position"),
        csvColumnName: "Position",
      },
      {
        key: "birthDate",
        type: UploadCentreColumnType.DATE,
        isRequired: false,
        label: `${translate("birthDate")} (dd/mm/yyyy)`,
        csvColumnName: "Birth Date (dd/mm/yyyy)",
      },
    ],
  };

  return COLUMNS;
};

export default GET_INPUT_COLUMNS;
